import { NameplatePool } from "../nameplates/pool.ts";

/**
 * The bubble pool (P1.9-T23; ADR-068 decision 9; ADR-061 decision 3, "chat bubbles come from pools"): one bubble a
 * speaker, held by the speaker's handle in the nameplates' pool, whose preallocated arrays make taking, finding and
 * returning a bubble allocate nothing once it has grown. A line starts its speaker's bubble, or replaces the text of
 * the one it holds and restarts its time; the bubble is kept only on frames that see its speaker drawn with its time
 * still running, and goes back at the frame's sweep otherwise, to be the next speaker's before the factory makes
 * another. Nothing is made until the first line, so the bubbles the UI layer holds are the most speakers ever
 * shown at once plus the frame's new ones.
 *
 * @example
 * ```ts
 * const bubbles = new BubblePool(() => makeBubble());
 * bubbles.start(line.handle, frame, time + durationMs("bubble-min"));
 * const bubble = bubbles.keep(handle, frame, time);
 * bubbles.sweep(frame, (gone) => gone.hide());
 * ```
 */

/**
 * What the pool asks of a bubble: the scene time its line shows until.
 */
export interface TimedBubble {
  until: number;
}

/**
 * A pool of bubbles, one per speaker shown.
 */
export class BubblePool<T extends TimedBubble> {
  private readonly held: NameplatePool<T>;

  /**
   * @param create Makes a new bubble, when a line comes and no returned bubble waits.
   */
  public constructor(create: () => T) {
    this.held = new NameplatePool<T>(create);
  }

  /**
   * Bubbles held by speakers now.
   *
   * @returns The count.
   */
  public get active(): number {
    return this.held.active;
  }

  /**
   * Returned bubbles waiting for the next speaker.
   *
   * @returns The count.
   */
  public get free(): number {
    return this.held.free;
  }

  /**
   * Bubbles the factory has made, the most speakers shown at once so far.
   *
   * @returns The count.
   */
  public get created(): number {
    return this.held.created;
  }

  /**
   * Starts a speaker's bubble for a line: the one it holds, else a returned one, else a new one, running until the
   * time given. A start is not a sighting: a bubble the speaker did not hold before is kept through the frame's
   * sweep only when `keep` finds its speaker drawn on the frame, and one it held keeps whatever sighting it had.
   *
   * @param handle The speaker's handle.
   * @param frame The frame the line is taken on.
   * @param until The scene time the line shows until, in ms.
   * @returns The speaker's bubble.
   * @throws RangeError when the handle is not one a snapshot carries (0 to 65535).
   */
  public start(handle: number, frame: number, until: number): T {
    const bubble = this.held.get(handle) ?? this.held.take(handle, frame - 1);
    bubble.until = until;
    return bubble;
  }

  /**
   * The bubble a speaker drawn on a frame holds, kept through the frame's sweep while its time runs.
   *
   * @param handle The speaker's handle.
   * @param frame The frame being drawn.
   * @param now The frame's scene time, in ms.
   * @returns The bubble, or undefined when the speaker holds none or its time has run out, which the sweep then
   *   returns.
   */
  public keep(handle: number, frame: number, now: number): T | undefined {
    const bubble = this.held.get(handle);
    if (bubble === undefined || bubble.until <= now) {
      return undefined;
    }
    this.held.take(handle, frame);
    return bubble;
  }

  /**
   * The bubble a speaker holds, whether or not its time runs or it is drawn.
   *
   * @param handle The speaker's handle.
   * @returns The bubble, or undefined when the speaker holds none.
   */
  public find(handle: number): T | undefined {
    return this.held.get(handle);
  }

  /**
   * Returns every bubble not kept on a frame: its speaker was not drawn, or its time ran out.
   *
   * @param frame The frame just drawn.
   * @param release Told each bubble returned and the handle that held it, so it can hide the bubble.
   */
  public sweep(frame: number, release: (bubble: T, handle: number) => void): void {
    this.held.sweep(frame, release);
  }

  /**
   * Visits every bubble a speaker holds.
   *
   * @param visit Told each bubble and its speaker's handle.
   */
  public forEachActive(visit: (bubble: T, handle: number) => void): void {
    this.held.forEachActive(visit);
  }
}
